import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Task, FullProject, ProjectMetadata } from '../types';
import { getCategoriesFromMetadata } from '../components/modals/overview/helpers';
import { extractRawTaskId, resolveScopedTaskById } from '../utils/idNormalizer';
import { isProjectEditableByUser, canUserMoveTaskBetweenProjects } from '../utils/projectPermissions';
import { extractUniqueWorkspaceAssignees } from './appState/searchMetadataAndHolidayMatchers';

interface UseAppLifecycleAndSelectorsParams {
  allProjects: FullProject[];
  projects: FullProject[];
  allTasksGlobal: Task[];
  currentProjectId: string;
  currentUserId: string;
  userEmail?: string | null;
  activeWorkspace: 'professional' | 'private';
  globalMetadata: ProjectMetadata;
  isInitialLoadComplete: boolean;
  viewMode: string;
  isRightSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (val: boolean) => void;
  setShowOnboardingWizard: (val: boolean) => void;
  reorderTask: (
    taskId: string | number,
    targetProjectId: string,
    targetBucketId: string,
    overId?: string | number,
    position?: 'before' | 'after' | 'end',
    mode?: 'status' | 'date' | 'custom',
    explicitPrevSort?: number,
    explicitNextSort?: number,
    sourceProjectId?: string
  ) => void;
  updateProjectMetadata: (projectId: string, metadata: Partial<ProjectMetadata>) => void;
  historyPastLength: number;
}

export function useAppLifecycleAndSelectors({
  allProjects,
  projects,
  allTasksGlobal,
  currentProjectId,
  currentUserId,
  userEmail,
  activeWorkspace,
  globalMetadata,
  isInitialLoadComplete,
  viewMode,
  isRightSidebarOpen,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  setShowOnboardingWizard,
  reorderTask,
  updateProjectMetadata,
  historyPastLength,
}: UseAppLifecycleAndSelectorsParams) {
  // Mobile Detection Hook
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Global pointer down monitor for deferred Firestore sync merge
  useEffect(() => {
    const onGlobalPointerDown = () => {
      window.__isGWanttPointerDown = true;
    };
    const onGlobalPointerUp = () => {
      window.__isGWanttPointerDown = false;
      if (window.__pendingMerge) {
        window.__pendingMerge = false;
        if (typeof window.__flushPendingMerge === 'function') {
          window.__flushPendingMerge();
        }
      }
    };
    window.addEventListener('pointerdown', onGlobalPointerDown, true);
    window.addEventListener('pointerup', onGlobalPointerUp, true);
    window.addEventListener('pointercancel', onGlobalPointerUp, true);
    return () => {
      window.removeEventListener('pointerdown', onGlobalPointerDown, true);
      window.removeEventListener('pointerup', onGlobalPointerUp, true);
      window.removeEventListener('pointercancel', onGlobalPointerUp, true);
    };
  }, []);

  // Onboarding Wizard auto-trigger
  useEffect(() => {
    if (
      !isInitialLoadComplete ||
      !currentUserId ||
      currentUserId === 'e2e_local_uid' ||
      userEmail?.toLowerCase() === 'test_e2e@telfs.gv.at'
    ) {
      return;
    }

    const inboxId = `inbox_inforum_${currentUserId}`;
    const inboxProj = allProjects.find(p => p.id === inboxId);
    const onboardingCompleted = inboxProj?.metadata?.onboardingCompleted === true;

    const customProjects = allProjects.filter(p => {
      const id = p.id;
      return !id.startsWith('inbox_inforum') &&
        !id.startsWith('comm_internal') &&
        !['kommunikation', 'inbox_inforum', 'posteingang', 'communication', 'comm_internal'].includes(id) &&
        !id.startsWith('sample_') &&
        !p.isSample &&
        !p.metadata?.isSample &&
        p.userId === currentUserId &&
        !p.archived;
    });

    if (!onboardingCompleted && customProjects.length === 0) {
      const wizardSeenThisSession = sessionStorage.getItem('stratum_wizard_session_seen') === 'true';
      if (!wizardSeenThisSession) {
        setShowOnboardingWizard(true);
        sessionStorage.setItem('stratum_wizard_session_seen', 'true');
      }
    }
  }, [isInitialLoadComplete, allProjects, currentUserId, userEmail, setShowOnboardingWizard]);

  // Bidirectional sync for categories between LocalStorage and Firestore
  useEffect(() => {
    if (!globalMetadata) return;
    let changed = false;

    const firestoreProf = globalMetadata.categories_professional;
    if (Array.isArray(firestoreProf)) {
      const localProfRaw = localStorage.getItem('gwantt_categories_professional');
      const localProf = localProfRaw ? JSON.parse(localProfRaw) : [];
      if (JSON.stringify(localProf) !== JSON.stringify(firestoreProf)) {
        localStorage.setItem('gwantt_categories_professional', JSON.stringify(firestoreProf));
        changed = true;
      }
    }

    const firestorePriv = globalMetadata.categories_private;
    if (Array.isArray(firestorePriv)) {
      const localPrivRaw = localStorage.getItem('gwantt_categories_private');
      const localPriv = localPrivRaw ? JSON.parse(localPrivRaw) : [];
      if (JSON.stringify(localPriv) !== JSON.stringify(firestorePriv)) {
        localStorage.setItem('gwantt_categories_private', JSON.stringify(firestorePriv));
        changed = true;
      }
    }

    if (changed) {
      window.dispatchEvent(new Event('storage'));
    }
  }, [globalMetadata]);

  // Undo notification state
  const [showUndo, setShowUndo] = useState(false);
  const prevPastLen = useRef(historyPastLength);

  useEffect(() => {
    if (historyPastLength > prevPastLen.current) {
      setShowUndo(true);
      const timer = setTimeout(() => setShowUndo(false), 8000);
      prevPastLen.current = historyPastLength;
      return () => clearTimeout(timer);
    }
    prevPastLen.current = historyPastLength;
    if (historyPastLength === 0) setShowUndo(false);
  }, [historyPastLength]);

  // Responsive container height & sidebar auto-collapse
  const [containerHeight, setContainerHeight] = useState(() => window.innerHeight - 150);

  useEffect(() => {
    const updateHeight = () => {
      const isProject = [
        'kanban', 'gantt', 'list', 'timeline', 'mindmap', 'projectContact',
        'calendar', 'concept', 'conceptBuilder', 'report', 'analysis',
        'globalKanban', 'communication', 'impactEffort', 'metricsDashboard',
        'globalCalendar', 'globalGantt'
      ].includes(viewMode);
      const headerHeight = isProject ? 116 : 64;
      setContainerHeight(window.innerHeight - headerHeight - 1);
      if (window.innerWidth >= 768 && window.innerWidth < 1180 && isRightSidebarOpen && !isSidebarCollapsed) {
        setIsSidebarCollapsed(true);
      }
    };

    let rafId: number;
    const debouncedUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateHeight);
    };

    updateHeight();
    window.addEventListener('resize', debouncedUpdate);
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      cancelAnimationFrame(rafId);
    };
  }, [viewMode, isRightSidebarOpen, isSidebarCollapsed, setIsSidebarCollapsed]);

  // Selectors
  const mobileTasks = useMemo(() => {
    if (!isMobile) return [];
    const projMap = new Map(allProjects.map(p => [p.id, p]));
    return allTasksGlobal.filter(t => {
      if (!t.name || t.name.trim() === '') return false;
      if (t.archived) return false;
      const parentProj = projMap.get(t.projectId || '');
      if (!parentProj || parentProj.archived) return false;
      if (t.projectId === 'comm_internal') {
        return (t.workspace || 'professional') === activeWorkspace;
      }
      return (parentProj.workspace || 'professional') === activeWorkspace;
    });
  }, [isMobile, allTasksGlobal, allProjects, activeWorkspace]);

  const categories = useMemo(
    () => getCategoriesFromMetadata(globalMetadata, activeWorkspace),
    [globalMetadata, activeWorkspace]
  );

  const isProjectEditor = useMemo(() => {
    if (!currentProjectId) return true;
    if (['dashboard', 'metricsDashboard', 'globalKanban', 'communication', 'impactEffort', 'noDateTasks', 'globalCalendar', 'projectPool', 'analysis', 'aiAssistant'].includes(currentProjectId)) return true;
    const project = allProjects.find(p => p.id === currentProjectId) || projects.find(p => p.id === currentProjectId);
    return isProjectEditableByUser(project, currentUserId, userEmail);
  }, [allProjects, projects, currentProjectId, currentUserId, userEmail]);

  const workspaceProjects = useMemo(
    () => allProjects.filter(p => (p.workspace || 'professional') === activeWorkspace),
    [allProjects, activeWorkspace]
  );

  const otherWorkspaceProjects = useMemo(
    () => allProjects.filter(p => (p.workspace || 'professional') !== activeWorkspace),
    [allProjects, activeWorkspace]
  );

  const dropdownProjects = useMemo(() => {
    return allProjects.filter(p => {
      const id = p.id.toLowerCase();
      return !id.startsWith('inbox_inforum') &&
        !id.startsWith('comm_internal') &&
        !['kommunikation', 'inbox_inforum', 'posteingang', 'communication', 'comm_internal'].includes(id) &&
        !['Kommunikation', 'Posteingang', 'Inbox'].includes(p.name) &&
        !p.archived &&
        (p.workspace || 'professional') === activeWorkspace;
    });
  }, [allProjects, activeWorkspace]);

  const workspaceTasksGlobal = useMemo(() => {
    const wsProjectIds = new Set<string>();
    allProjects.forEach(p => {
      if ((p.workspace || 'professional') === activeWorkspace) {
        wsProjectIds.add(p.id);
        if (p.rawId) wsProjectIds.add(p.rawId);
      }
    });
    return allTasksGlobal.filter(t => {
      if (t.workspace) return t.workspace === activeWorkspace;
      if (t.projectId && allProjects.length > 0) {
        return wsProjectIds.has(t.projectId);
      }
      return activeWorkspace === 'professional';
    });
  }, [allTasksGlobal, allProjects, activeWorkspace]);

  const globalAllAssignees = useMemo(
    () => extractUniqueWorkspaceAssignees(workspaceTasksGlobal, allProjects, activeWorkspace),
    [workspaceTasksGlobal, allProjects, activeWorkspace]
  );

  const globalAvailableTypes = useMemo(() => {
    const types = new Set<string>();
    workspaceTasksGlobal.forEach(t => {
      if (t.type) types.add(t.type);
    });
    return Array.from(types);
  }, [workspaceTasksGlobal]);

  const globalAvailableStatuses = useMemo(() => {
    const statuses = new Set<string>();
    workspaceTasksGlobal.forEach(t => {
      const s = t.status || (t.completed ? 'completed' : 'open');
      statuses.add(s);
    });
    return Array.from(statuses);
  }, [workspaceTasksGlobal]);

  const handleReorderTask = useCallback((
    taskId: string | number,
    targetProjectId: string,
    targetBucketId: string,
    overId?: string | number,
    position: 'before' | 'after' | 'end' = 'end',
    mode: 'status' | 'date' | 'custom' = 'status',
    explicitPrevSort?: number,
    explicitNextSort?: number
  ) => {
    let realTaskId = taskId;
    let realOverId = overId;
    let sourceProjectId: string | undefined = undefined;

    const existingTask = resolveScopedTaskById(taskId, {
      projects: allProjects,
      preferredProjectId: targetProjectId,
    });
    if (existingTask) {
      realTaskId = existingTask.id;
      sourceProjectId = existingTask.projectId;
    } else {
      realTaskId = extractRawTaskId(taskId, targetProjectId);
    }

    // H-1: Block reordering or cross-project moving if user lacks write permission on source or target project
    if (
      !canUserMoveTaskBetweenProjects(
        existingTask,
        targetProjectId,
        allProjects,
        currentUserId,
        userEmail
      )
    ) {
      return;
    }

    if (overId) {
      const existingOver = resolveScopedTaskById(overId, {
        projects: allProjects,
        preferredProjectId: targetProjectId,
      });
      if (existingOver) {
        realOverId = existingOver.id;
      } else {
        realOverId = extractRawTaskId(overId, targetProjectId);
      }
    }

    reorderTask(realTaskId, targetProjectId, targetBucketId, realOverId, position, mode, explicitPrevSort, explicitNextSort, sourceProjectId);
  }, [allProjects, currentUserId, userEmail, reorderTask]);

  const handleAddToHistory = useCallback((projectId: string, entry: { title: string; content: string; type: string }) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) return;

    const metadata = project.metadata || {};
    const historyLogs = metadata.meetingLogs || [];

    const newEntry = {
      ...entry,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };

    updateProjectMetadata(projectId, {
      ...metadata,
      meetingLogs: [newEntry, ...historyLogs]
    });
  }, [allProjects, updateProjectMetadata]);

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dStr = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dStr}`;
  }, []);

  return {
    isMobile,
    showUndo,
    setShowUndo,
    containerHeight,
    mobileTasks,
    categories,
    isProjectEditor,
    workspaceProjects,
    otherWorkspaceProjects,
    dropdownProjects,
    globalAllAssignees,
    globalAvailableTypes,
    globalAvailableStatuses,
    handleReorderTask,
    handleAddToHistory,
    todayStr,
  };
}
